import React from 'react';
import {
  CloseOutlined,
  SmartDisplayOutlined,
  KeyboardBackspaceOutlined,
} from '@mui/icons-material';
import { HelpIcon } from 'app/components/core/icons';
import { FeatureEnum } from 'app/modules/Segment/types';
import { getYouTubeEmbedUrl } from './helpers';
import {
  emitBackNavigationClickEvent,
  emitHelpNavigationClickEvent,
  emitTutorialNavigationClickEvent,
  handleBackNavigationClick,
  handleHelpNavigationClick,
  handleTutorialNavigationClick,
  HeaderNavigationTrackingEvent,
} from './handlers';
import * as S from './styles';

export interface HeaderNavigationProps {
  backLabel?: string;
  backButtonAriaLabel?: string;
  tutorialVideoUrl?: string;
  className?: string;
  helpButtonAriaLabel?: string;
  helpUrl?: string;
  tutorialButtonAriaLabel?: string;
  tutorialButtonLabel?: string;
  feature?: FeatureEnum;
  featureContext?: string;
  isSecondaryScreen?: boolean;
  pageTitle?: string;
  onBackClick?: () => void;
  onHelpClick?: () => void;
  onTutorialClick?: () => void;
  onTrackEvent?: (event: HeaderNavigationTrackingEvent) => void;
}

const HeaderNavigation: React.FC<HeaderNavigationProps> = ({
  backLabel,
  pageTitle,
  tutorialButtonLabel = 'Assistir tutorial',
  tutorialVideoUrl,
  backButtonAriaLabel = 'Voltar',
  helpButtonAriaLabel = 'Ajuda',
  helpUrl,
  tutorialButtonAriaLabel = 'Assistir tutorial',
  feature,
  featureContext,
  isSecondaryScreen,
  onBackClick,
  onHelpClick,
  onTrackEvent,
  onTutorialClick,
  className,
}) => {
  const [isTutorialOpen, setIsTutorialOpen] = React.useState(false);
  const [isTutorialLoading, setIsTutorialLoading] = React.useState(true);
  const tutorialEmbedUrl = React.useMemo(
    () => getYouTubeEmbedUrl(tutorialVideoUrl),
    [tutorialVideoUrl],
  );
  const shouldRenderTutorialButton = Boolean(
    onTutorialClick || tutorialEmbedUrl,
  );

  const emitEventAndRunAction = ({
    emitEvent,
    customAction,
    defaultAction,
  }: {
    emitEvent: () => void;
    customAction?: () => void;
    defaultAction: () => void;
  }) => {
    // Tracking always runs first. A custom callback only replaces the default action.
    emitEvent();

    if (customAction) {
      customAction();
      return;
    }

    defaultAction();
  };

  const handleBackClick = () => {
    emitEventAndRunAction({
      emitEvent: () =>
        emitBackNavigationClickEvent({
          onTrackEvent,
          feature,
          featureContext,
          pageTitle,
          isSecondaryScreen,
        }),
      customAction: onBackClick,
      defaultAction: () => handleBackNavigationClick(),
    });
  };

  const handleTutorialClick = () => {
    emitEventAndRunAction({
      emitEvent: () =>
        emitTutorialNavigationClickEvent({
          tutorialVideoUrl,
          onTrackEvent,
          feature,
          featureContext,
          pageTitle,
          isSecondaryScreen,
        }),
      customAction: onTutorialClick,
      defaultAction: () =>
        handleTutorialNavigationClick({
          tutorialVideoUrl,
          tutorialEmbedUrl,
          setIsTutorialLoading,
          setIsTutorialOpen,
        }),
    });
  };

  const handleHelpClick = () => {
    emitEventAndRunAction({
      emitEvent: () =>
        emitHelpNavigationClickEvent({
          helpUrl,
          onTrackEvent,
          featureContext,
          pageTitle,
          isSecondaryScreen,
        }),
      customAction: onHelpClick,
      defaultAction: () =>
        handleHelpNavigationClick({
          helpUrl,
        }),
    });
  };

  const handleCloseTutorial = () => {
    setIsTutorialOpen(false);
    setIsTutorialLoading(true);
  };

  return (
    <>
      <S.Container className={className}>
        <S.Content>
          {backLabel && (
            <S.SecondaryButton
              type="button"
              onClick={handleBackClick}
              aria-label={backButtonAriaLabel}
            >
              <S.ButtonIconSlot>
                <KeyboardBackspaceOutlined
                  sx={{ fontSize: 18, fill: '#5E5E60' }}
                />
              </S.ButtonIconSlot>
              <S.Title>{backLabel}</S.Title>
            </S.SecondaryButton>
          )}
          <S.Actions>
            {shouldRenderTutorialButton && (
              <S.SecondaryButton
                type="button"
                onClick={handleTutorialClick}
                aria-label={tutorialButtonAriaLabel}
              >
                <S.ButtonIconSlot>
                  <SmartDisplayOutlined
                    sx={{ fontSize: 18, fill: '#5E5E60' }}
                  />
                </S.ButtonIconSlot>
                <span>{tutorialButtonLabel}</span>
              </S.SecondaryButton>
            )}

            <S.SecondaryButton
              type="button"
              onClick={handleHelpClick}
              aria-label={helpButtonAriaLabel}
            >
              <S.HelpButtonIconSlot>
                <HelpIcon size={18} color="#5E5E60" />
              </S.HelpButtonIconSlot>
              <span>Ajuda</span>
            </S.SecondaryButton>
          </S.Actions>
        </S.Content>
      </S.Container>

      <S.TutorialModal
        open={isTutorialOpen}
        onClose={handleCloseTutorial}
        aria-labelledby="header-navigation-tutorial-title"
      >
        <S.TutorialModalContent>
          <S.TutorialModalHeader>
            <S.TutorialModalTitle id="header-navigation-tutorial-title">
              Tutorial
            </S.TutorialModalTitle>
            <S.TutorialModalCloseButton
              aria-label="Fechar tutorial"
              onClick={handleCloseTutorial}
            >
              <CloseOutlined sx={{ fontSize: 20 }} />
            </S.TutorialModalCloseButton>
          </S.TutorialModalHeader>
          <S.TutorialIframeWrapper $isLoading={isTutorialLoading}>
            {tutorialEmbedUrl ? (
              <S.TutorialIframe
                src={tutorialEmbedUrl}
                title={tutorialButtonLabel}
                onLoad={() => setIsTutorialLoading(false)}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : null}
          </S.TutorialIframeWrapper>
        </S.TutorialModalContent>
      </S.TutorialModal>
    </>
  );
};

export default HeaderNavigation;
